/**
 * 展立改键：设备按键 ↔ 子应用统一格式（协议 type 0x50 DPI / 0x20 鼠标键）
 */
import { MouseKeyFunction } from "../commonApi";
import type { ZhanliMouseKey } from "./deviceProtocol";
import { ZHANLI_MACRO_DEVICE_SLOTS } from "./macroUtility";

const ZHANLI_T_HID = 0x20;
const ZHANLI_T_MM = 0x21;
const ZHANLI_T_MM48 = 0x30;
const ZHANLI_T_DPI = 0x50;
/** demo 组合键 / 键盘单键（无修饰时 code1=0） */
const ZHANLI_T_COMBO = 0x80;
/** 展立火力/连发（keys.json type=64） */
const ZHANLI_T_FIRE = 0x40;
/** 展立协议 KEYCODE_CONTROL：松开后 scene_index=(+1)%4 */
const ZHANLI_T_CONTROL = 0x70;
/** 展立宏 KEYCODE_MACRO */
const ZHANLI_T_MACRO = 0xa0;

/** 展立 HID 鼠标键 code1（左/右/中/后退/前进） */
const ZHANLI_MOUSE_HID_CODE1 = new Set([1, 2, 4, 8, 16]);

/**
 * 滚轮槽与其它物理键同一套写入路径，不再按槽位改写 type。
 * 保留导出以免外部引用断裂。
 */
export function applyZhanliScrollSlotMouseBinding(
  _idx: number,
  key: ZhanliMouseKey
): ZhanliMouseKey {
  return key;
}

/**
 * 滚轮槽改绑鼠标键时，部分固件仍保留 MM type，仅改 code1（如 type=33,code1=2）。
 * 注意：设备读回的滚轮上也是 code1=1,code2=0，须先走滚轮解析再调用本函数。
 */
function isZhanliMmSlotMouseHidBinding(
  code1: number,
  code2: number,
  code3: number
): boolean {
  const c1 = code1 & 0xff;
  const c2 = code2 & 0xff;
  const c3 = code3 & 0xff;
  // 不含 1：与滚轮上读回 (code1=1,code2=0) 冲突，左键改绑走标准 HID type=0x20
  return (
    (c1 === 2 || c1 === 4 || c1 === 8 || c1 === 16) && c2 === 0 && c3 === 0
  );
}

/** 部分固件用 0x40+n 编码鼠标键：0x40 空闲 / 0x41 左 / 0x42 右 / 0x43 中 / 0x44 后退 / 0x45 前进 */
function zhanliAltMouseCode1ToHidBit(code1: number): number | null {
  const c1 = code1 & 0xff;
  if (c1 === 0x40) return 0;
  if (c1 >= 0x41 && c1 <= 0x45) return 1 << (c1 - 0x41);
  return null;
}

export type ZhanliScrollDirection = "up" | "down";

/**
 * 组合键(type=128)规范：code1=修饰位(≤0x0f)、code2=主键(HID)。
 * 部分读回会对调 / 残留垃圾，纠正后再写 store / 打标签。
 *
 * 注意：字母 A–L 的 HID(0x04–0x0F) 与修饰掩码空间重叠；
 * 若误把 HID 放进 code1 且 code2=0，L(0x0F) 会被解码成 Ctrl+Shift+Alt+Win 全开。
 */
function canonicalizeZhanliComboCodes(
  code1: number,
  code2: number
): { code1: number; code2: number } {
  const c1 = code1 & 0xff;
  const c2 = code2 & 0xff;

  // 主键在 code2，code1 超出修饰掩码（如 0xFF）→ 清掉垃圾修饰位
  if (c1 > 0x0f && c2 >= 4 && c2 < 0xe0) {
    return { code1: 0, code2: c2 };
  }

  // 主键误放在 code1、code2 为空：归一成无修饰单键（修复 A–L 误亮全修饰）
  if (c2 === 0 && c1 >= 4 && c1 < 0xe0) {
    return { code1: 0, code2: c1 };
  }

  // 对调读回：code1 像主键、code2 像修饰掩码
  const modsOk = c1 <= 0x0f;
  const keyOk = c2 >= 4 && c2 < 0xe0;
  if (!modsOk && !keyOk && c1 >= 4 && c1 < 0xe0 && c2 > 0 && c2 <= 0x0f) {
    return { code1: c2, code2: c1 };
  }

  // 修饰位只保留低 4 bit
  return { code1: c1 & 0x0f, code2: c2 };
}

/** 组合键/键盘单键 store：zhanli,128,mods,hid,0 */
function zhanliComboToStoreValue(
  code1: number,
  code2: number,
  code3 = 0
): (string | number)[] {
  const combo = canonicalizeZhanliComboCodes(code1, code2);
  return ["zhanli", ZHANLI_T_COMBO, combo.code1, combo.code2, code3 & 0xff];
}

/** 解析修饰位 + 主键（HID 在 code2；兼容极少见的 code3 残留） */
function resolveZhanliComboMain(
  code1: number,
  code2: number,
  code3 = 0
): { mods: number; main: number } {
  const mainRaw = (code2 > 0 ? code2 : code3) & 0xff;
  const combo = canonicalizeZhanliComboCodes(code1, mainRaw);
  return {
    mods: combo.code1,
    main: combo.code2,
  };
}

/** 从协议字节解析滚轮方向；非滚轮键返回 null */
function parseZhanliScrollDirectionFromProtocol(
  type: number,
  code1: number,
  code2: number
): ZhanliScrollDirection | null {
  const t = type & 0xff;
  const c1 = code1 & 0xff;
  const c2 = code2 & 0xff;
  if (t !== ZHANLI_T_MM && t !== ZHANLI_T_MM48) return null;

  // keys.json / 写入预设：code1=56, code2=1|255
  if (c1 === 56 && (c2 === 1 || c2 === 255)) {
    return c2 === 1 ? "up" : "down";
  }
  // 设备读回：code1=1|255, code2=0
  if (c2 === 0 && c1 === 1) return "up";
  if (c2 === 0 && c1 === 255) return "down";

  return null;
}

function zhanliScrollDirectionToStoreCode2(dir: ZhanliScrollDirection): number {
  return dir === "up" ? 1 : 255;
}

function zhanliScrollWheelToStoreValue(
  code2: number,
  code3 = 0
): (string | number)[] {
  const dir: ZhanliScrollDirection = (code2 & 0xff) === 255 ? "down" : "up";
  return [
    "zhanli",
    ZHANLI_T_MM,
    56,
    zhanliScrollDirectionToStoreCode2(dir),
    code3 & 0xff,
  ];
}

/** 协议字节 → 滚轮 store；非滚轮返回 null */
function zhanliProtocolToScrollStoreValue(
  type: number,
  code1: number,
  code2: number,
  code3 = 0
): (string | number)[] | null {
  const dir = parseZhanliScrollDirectionFromProtocol(type, code1, code2);
  if (!dir) return null;
  return zhanliScrollWheelToStoreValue(
    zhanliScrollDirectionToStoreCode2(dir),
    code3
  );
}

/** 滚轮方向 → 设备协议字节（与恢复默认 / 抓包一致：type=0x30, code1=1|255） */
function zhanliScrollDirectionToProtocol(
  dir: ZhanliScrollDirection
): Pick<ZhanliMouseKey, "type" | "code1" | "code2" | "code3"> {
  return {
    type: ZHANLI_T_MM48,
    code1: dir === "down" ? 255 : 1,
    code2: 0,
    code3: 0,
  };
}
export function normalizeZhanliStoreKeyValue(
  keyValue: (string | number)[]
): (string | number)[] {
  if (!keyValue?.length) return keyValue;

  const typeStr = String(keyValue[0]).toLowerCase();
  if (typeStr === "zhanli" && keyValue.length >= 5) {
    const n1 = Number(keyValue[1]) & 0xff;
    const n2 = Number(keyValue[2]) & 0xff;
    const n3 = Number(keyValue[3]) & 0xff;
    const n4 = Number(keyValue[4]) & 0xff;
    const store = zhanliProtocolToScrollStoreValue(n1, n2, n3, n4);
    if (store) return store;
    if (
      (n1 === ZHANLI_T_MM || n1 === ZHANLI_T_MM48) &&
      isZhanliMmSlotMouseHidBinding(n2, n3, n4)
    ) {
      return ["0x01", zhanliHidCode1ToParamHex(n2)];
    }
    if (n1 === ZHANLI_T_HID || n1 === 32) {
      if (n2 === 0 && n3 === 0 && n4 === 0) {
        return [MouseKeyFunction.Disable, "0x0000"];
      }
      // type=0x20 + code1=0 + code2=HID → 键盘单键（demo 写为 type=128）
      if (n2 === 0 && n3 > 0) {
        return zhanliComboToStoreValue(0, n3, n4);
      }
      if (ZHANLI_MOUSE_HID_CODE1.has(n2) && n3 === 0 && n4 === 0) {
        return ["0x01", zhanliHidCode1ToParamHex(n2)];
      }
      const altHid = zhanliAltMouseCode1ToHidBit(n2);
      if (altHid != null && n3 === 0 && n4 === 0) {
        if (altHid === 0) return [MouseKeyFunction.Disable, "0x0000"];
        return ["0x01", zhanliHidCode1ToParamHex(altHid)];
      }
    }
    if (n1 === ZHANLI_T_COMBO || n1 === 128) {
      return zhanliComboToStoreValue(n2, n3, n4);
    }
    return keyValue;
  }

  // 协议裸数组 [type, code1, code2] / [type, code1, code2, code3]（profile 回退常见）
  const typeNum = Number(keyValue[0]);
  if (
    (typeNum === ZHANLI_T_HID || typeNum === 32) &&
    keyValue.length >= 2 &&
    keyValue.length <= 4
  ) {
    const c1 = Number(keyValue[1]) & 0xff;
    const c2 = Number(keyValue[2] ?? 0) & 0xff;
    const c3 = Number(keyValue[3] ?? 0) & 0xff;
    if (c1 === 0 && c2 === 0 && c3 === 0) {
      return [MouseKeyFunction.Disable, "0x0000"];
    }
    if (c1 === 0 && c2 > 0) {
      return zhanliComboToStoreValue(0, c2);
    }
    if (ZHANLI_MOUSE_HID_CODE1.has(c1) && c2 === 0 && c3 === 0) {
      return ["0x01", zhanliHidCode1ToParamHex(c1)];
    }
  }

  if (
    (typeNum === MouseKeyFunction.MouseKey ||
      String(keyValue[0]).toLowerCase() === "0x01") &&
    keyValue.length >= 2
  ) {
    const p = parseUnifiedParam(keyValue[1]);
    const code1Map: Record<number, number> = {
      0x0100: 1,
      0x0200: 2,
      0x0400: 4,
      0x0800: 8,
      0x1000: 16,
    };
    const code1 = code1Map[p];
    if (code1 != null) {
      return ["0x01", zhanliHidCode1ToParamHex(code1)];
    }
    // 0x4000 / 0x4100…：固件 0x40+n 编码被 <<8 后的中间态
    const altHid = zhanliAltMouseCode1ToHidBit((p >> 8) & 0xff);
    if (altHid != null && (p & 0xff) === 0) {
      if (altHid === 0) return [MouseKeyFunction.Disable, "0x0000"];
      return ["0x01", zhanliHidCode1ToParamHex(altHid)];
    }
  }

  if (typeStr === "1005" || Number(keyValue[0]) === 1005) {
    const code = parseUnifiedParam(keyValue[1]);
    const store = zhanliProtocolToScrollStoreValue(
      ZHANLI_T_MM,
      code & 0xff,
      (code >> 8) & 0xff,
      0
    );
    if (store) return store;
    const c1 = code & 0xff;
    const c2 = (code >> 8) & 0xff;
    if (isZhanliMmSlotMouseHidBinding(c1, c2, 0)) {
      return ["0x01", zhanliHidCode1ToParamHex(c1)];
    }
    if (c1 === 0) {
      const altHid = zhanliAltMouseCode1ToHidBit(c2);
      if (altHid != null) {
        if (altHid === 0) return [MouseKeyFunction.Disable, "0x0000"];
        return ["0x01", zhanliHidCode1ToParamHex(altHid)];
      }
    }
  }

  if (typeNum === MouseKeyFunction.UpDownRoll && keyValue.length >= 2) {
    const p = parseUnifiedParam(keyValue[1]);
    const code2 = p === 0x0100 ? 1 : p === 0x0200 ? 255 : null;
    if (code2 != null) return zhanliScrollWheelToStoreValue(code2);
  }

  // 统一层 ShortcutKey → demo type=128
  if (typeNum === MouseKeyFunction.ShortcutKey && keyValue.length >= 2) {
    const { mods, main } = resolveZhanliComboMain(
      Number(keyValue[1]) || 0,
      Number(keyValue[2]) || 0,
      Number(keyValue[3]) || 0
    );
    if (main > 0) return zhanliComboToStoreValue(mods, main);
  }

  return keyValue;
}

/** 展立宏 store [6, code1, code2, slot]：槽位在 code1 低 4 位或第 4 段（仅 0~3），勿把 code1 当槽号（如 32→M32） */
export function resolveZhanliMacroSlotFromStoreValue(
  keyValue: (string | number)[]
): number | null {
  if (!keyValue?.length) return null;
  const head = Number(keyValue[0]);
  if (
    head !== MouseKeyFunction.Macro &&
    head !== ZHANLI_T_MACRO &&
    String(keyValue[0]).toLowerCase() !== "6"
  ) {
    return null;
  }
  // 匠盟/demo：["6","宏名"] 无数字槽位
  const param = keyValue[1];
  if (typeof param === "string" && param.trim() && !/^0x/i.test(param)) {
    return null;
  }
  let slot: number;
  if (keyValue.length >= 4 && Number.isFinite(Number(keyValue[3]))) {
    slot = Number(keyValue[3]) & 0x0f;
  } else {
    slot = Number(keyValue[1]) & 0x0f;
  }
  if (!Number.isFinite(slot) || slot < 0 || slot >= ZHANLI_MACRO_DEVICE_SLOTS) {
    return null;
  }
  return slot;
}

/** 设备回读宏键 → 匠盟/demo 一致的 store：["6","宏名"] */
export function canonicalizeZhanliMacroStoreValue(
  keyValue: (string | number)[],
  macroName?: string | null
): (string | number)[] {
  const slot = resolveZhanliMacroSlotFromStoreValue(keyValue);
  if (slot == null) return keyValue;
  const param = keyValue[1];
  if (typeof param === "string" && param.trim() && !/^0x/i.test(param)) {
    return keyValue;
  }
  const trimmed = String(macroName ?? "").trim();
  return [keyValue[0], trimmed || `M${slot}`];
}

export function isZhanliMacroStoreValue(
  keyValue: (string | number)[]
): boolean {
  if (!keyValue?.length) return false;
  const head = Number(keyValue[0]);
  if (
    head !== MouseKeyFunction.Macro &&
    head !== ZHANLI_T_MACRO &&
    String(keyValue[0]).toLowerCase() !== "6"
  ) {
    return false;
  }
  const param = keyValue[1];
  if (typeof param === "string" && param.trim() && !/^0x/i.test(param)) {
    return true;
  }
  return resolveZhanliMacroSlotFromStoreValue(keyValue) != null;
}

/**
 * 展立宏键展示名：优先 store 内 ["6","名称"]，再查槽位 0~3 对应宏名，最后 M{n}
 */
export function resolveZhanliMacroDisplayName(
  keyValue: (string | number)[],
  options?: {
    macroNameResolver?: (slot: number) => string | null | undefined;
  }
): string | null {
  if (!keyValue?.length) return null;
  const normalized = normalizeZhanliStoreKeyValue(keyValue);
  if (!isZhanliMacroStoreValue(normalized)) return null;

  const existing = readZhanliMacroNameFromStoreValue(normalized);
  if (existing) return existing;

  const slot = resolveZhanliMacroSlotFromStoreValue(normalized);
  if (slot == null) return null;

  const resolved = options?.macroNameResolver?.(slot)?.trim();
  if (resolved) return resolved;
  return `M${slot}`;
}

/** 从板载快照 config.macros 或 mouseCfg.macros 构建宏名解析器 */
export function buildZhanliMacroNameResolver(
  macros?: Array<{ name?: string } | null> | null,
  fallback?: (slot: number) => string | null | undefined
): (slot: number) => string | null {
  return (slot: number) => {
    if (
      !Number.isFinite(slot) ||
      slot < 0 ||
      slot >= ZHANLI_MACRO_DEVICE_SLOTS
    ) {
      return null;
    }
    const entry = macros?.[slot];
    const fromMacros =
      typeof entry?.name === "string" && entry.name.trim()
        ? entry.name.trim()
        : null;
    if (fromMacros) return fromMacros;
    const fromFallback = fallback?.(slot)?.trim();
    return fromFallback || null;
  };
}

/** 社区预览：展立板载现为 bin 快照，无法从包内解析 macros */
export function readZhanliMacrosFromBoardSnapshot(
  _snapshot: unknown
): Array<{ name?: string }> | undefined {
  return undefined;
}

/** 读 store 中已写入的宏名（["6","名称"]） */
export function readZhanliMacroNameFromStoreValue(
  keyValue: (string | number)[]
): string | null {
  const param = keyValue?.[1];
  if (typeof param === "string" && param.trim() && !/^0x/i.test(param)) {
    return param.trim();
  }
  return null;
}

/**
 * 单键 store 规范化：normalize + 宏键 canonical 为 ["6","宏名"]
 * 非宏键仅 normalize（对齐 demo resolveMouseKeyLang 前的 type/code 统一）
 */
export function finalizeZhanliKeyStoreValue(
  keyValue: (string | number)[]
): (string | number)[] {
  const normalized = normalizeZhanliStoreKeyValue(keyValue);
  if (!isZhanliMacroStoreValue(normalized)) return normalized;
  const existingName = readZhanliMacroNameFromStoreValue(normalized);
  if (existingName) return [normalized[0], existingName];
  const slot = resolveZhanliMacroSlotFromStoreValue(normalized);
  if (slot == null) return normalized;
  return canonicalizeZhanliMacroStoreValue(normalized, `M${slot}`);
}

function zhanliHidCode1ToParamHex(code1: number): string {
  const n = code1 & 0xff;
  const map: Record<number, number> = {
    1: 0x0100,
    2: 0x0200,
    4: 0x0400,
    8: 0x0800,
    16: 0x1000,
  };
  const p = map[n] ?? n << 8;
  return `0x${p.toString(16).padStart(4, "0")}`;
}

function zhanliUsageBytesTo1005(
  c1: number,
  c2: number,
  c3: number
): (string | number)[] {
  const usage = (c1 & 0xff) | ((c2 & 0xff) << 8) | ((c3 & 0xff) << 16);
  return ["1005", `0x${usage.toString(16).padStart(4, "0")}`];
}

export function parseUnifiedParam(param: unknown): number {
  if (param == null) return 0;
  if (typeof param === "number" && Number.isFinite(param)) return param >>> 0;
  const s = String(param).trim();
  if (/^0x/i.test(s)) return parseInt(s, 16) >>> 0;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n >>> 0 : 0;
}

export function zhanliMouseKeyToStoreValue(
  k: ZhanliMouseKey
): (string | number)[] {
  const t = Number(k.type) & 0xff;
  const c1 = Number(k.code1) & 0xff;
  const c2 = Number(k.code2) & 0xff;
  const c3 = Number(k.code3) & 0xff;

  if (t === MouseKeyFunction.FireKey) {
    return [MouseKeyFunction.FireKey, c1, c2];
  }
  // 展立协议火力键 type=0x40：统一走特殊 Tab 自定义火力（code1=间隔 code2=次数）
  if (t === ZHANLI_T_FIRE) {
    return [MouseKeyFunction.FireKey, c1, c2];
  }
  if (t === ZHANLI_T_HID || t === 32) {
    if (c1 === 0) {
      // code2>0：键盘单键；否则禁用
      if (c2 > 0) return zhanliComboToStoreValue(0, c2);
      return [MouseKeyFunction.Disable, "0x0000"];
    }
    const altHid = zhanliAltMouseCode1ToHidBit(c1);
    if (altHid != null) {
      if (altHid === 0) return [MouseKeyFunction.Disable, "0x0000"];
      return ["0x01", zhanliHidCode1ToParamHex(altHid)];
    }
    if (ZHANLI_MOUSE_HID_CODE1.has(c1)) {
      return ["0x01", zhanliHidCode1ToParamHex(c1)];
    }
    // 未知 HID code1（如误读 0x40→0x4000）勿直接展示原始码，按禁用兜底
    return [MouseKeyFunction.Disable, "0x0000"];
  }
  if (t === ZHANLI_T_MM || t === ZHANLI_T_MM48) {
    const store = zhanliProtocolToScrollStoreValue(t, c1, c2, c3);
    if (store) return store;
    if (isZhanliMmSlotMouseHidBinding(c1, c2, c3)) {
      return ["0x01", zhanliHidCode1ToParamHex(c1)];
    }
    // MM 槽读回 0x40+n 鼠标键编码（usage 低字节=0、高字节=0x40+n → 0x4000 等）
    if (c1 === 0 && c3 === 0) {
      const altHid = zhanliAltMouseCode1ToHidBit(c2);
      if (altHid != null) {
        if (altHid === 0) return [MouseKeyFunction.Disable, "0x0000"];
        return ["0x01", zhanliHidCode1ToParamHex(altHid)];
      }
    }
    const altOnC1 = zhanliAltMouseCode1ToHidBit(c1);
    if (altOnC1 != null && c2 === 0 && c3 === 0) {
      if (altOnC1 === 0) return [MouseKeyFunction.Disable, "0x0000"];
      return ["0x01", zhanliHidCode1ToParamHex(altOnC1)];
    }
    return zhanliUsageBytesTo1005(c1, c2, c3);
  }
  if (t === ZHANLI_T_DPI || t === 80) {
    // keys.json：code1 1=DPI循环 / 2=DPI+ / 3=DPI-
    const code1ToParam: Record<number, number> = {
      1: 0x0100,
      2: 0x0200,
      3: 0x0300,
    };
    const param = code1ToParam[c1];
    if (param != null) {
      return ["0x02", `0x${param.toString(16).padStart(4, "0")}`];
    }
    return ["zhanli", t, c1, c2, c3];
  }
  if (t === ZHANLI_T_COMBO || t === 128 || t === 144) {
    if (t === 144) return ["zhanli", t, c1, c2, c3];
    return zhanliComboToStoreValue(c1, c2, c3);
  }
  if (t === ZHANLI_T_CONTROL || t === 112) {
    // KEYCODE_CONTROL：配置循环切换（bytes 70 00 00）
    return [MouseKeyFunction.ProfileSwitch, "0x0500"];
  }
  if (t === ZHANLI_T_MACRO || t === 0xa0) {
    // 设备协议每键仅 3 字节，无 code3；宏槽在 code1 低 4 位（与宏索引 1:1）
    const slot = c1 & 0x0f;
    return [MouseKeyFunction.Macro, c1, c2, slot];
  }
  return ["zhanli", t, c1, c2, c3];
}

export function unifiedKeyFunctionToZhanliMouseKey(
  prev: ZhanliMouseKey,
  kf: { type: number; param?: number | number[] | string | undefined }
): ZhanliMouseKey {
  const t = Number(kf.type);

  if (t === MouseKeyFunction.DPISwitch) {
    const p = parseUnifiedParam(kf.param);
    const paramToCode1: Record<number, number> = {
      0x0100: 1, // DPI循环
      0x0200: 2, // DPI+
      0x0300: 3, // DPI-
    };
    const code1 = paramToCode1[p] ?? 1;
    return { ...prev, type: ZHANLI_T_DPI, code1, code2: 0, code3: 0 };
  }

  if (t === MouseKeyFunction.ProfileSwitch) {
    // 展立仅支持循环切换：KEYCODE_CONTROL 0x70 00 00
    return {
      ...prev,
      type: ZHANLI_T_CONTROL,
      code1: 0,
      code2: 0,
      code3: 0,
    };
  }

  if (t === 1005) {
    const code = parseUnifiedParam(kf.param);
    const code1 = code & 0xff;
    const code2 = (code >> 8) & 0xff;
    const code3 = (code >> 16) & 0xff;
    const dir = parseZhanliScrollDirectionFromProtocol(
      ZHANLI_T_MM,
      code1,
      code2
    );
    if (dir) {
      return { ...prev, ...zhanliScrollDirectionToProtocol(dir) };
    }
    // 其余 1005 载荷（多媒体等）：按设备侧 MM48 族下发
    return { ...prev, type: ZHANLI_T_MM48, code1, code2, code3 };
  }

  if (t === MouseKeyFunction.UpDownRoll) {
    const p = parseUnifiedParam(kf.param);
    const dir: ZhanliScrollDirection = p === 0x0200 ? "down" : "up";
    return { ...prev, ...zhanliScrollDirectionToProtocol(dir) };
  }

  if (t === MouseKeyFunction.MouseKey || t === 0x01) {
    const p = parseUnifiedParam(kf.param);
    const code1Map: Record<number, number> = {
      0x0100: 1,
      0x0200: 2,
      0x0400: 4,
      0x0800: 8,
      0x1000: 16,
    };
    return {
      ...prev,
      type: ZHANLI_T_HID,
      code1: code1Map[p] ?? (p >> 8 || 1),
      code2: 0,
      code3: 0,
    };
  }

  if (t === MouseKeyFunction.Disable) {
    return { ...prev, type: ZHANLI_T_HID, code1: 0, code2: 0, code3: 0 };
  }

  // 键盘/组合键：统一 type=0x80（ShortcutKey 与 zhanli,128 同路径）
  if (t === MouseKeyFunction.ShortcutKey || t === ZHANLI_T_COMBO || t === 128) {
    if (Array.isArray(kf.param) && kf.param.length >= 1) {
      const { mods, main } = resolveZhanliComboMain(
        Number(kf.param[0]) || 0,
        Number(kf.param[1]) || 0,
        Number(kf.param[2]) || 0
      );
      return {
        ...prev,
        type: ZHANLI_T_COMBO,
        code1: mods,
        code2: main,
        code3: 0,
      };
    }
  }

  if (t === MouseKeyFunction.Macro) {
    const p = kf.param;
    if (Array.isArray(p)) {
      const code1Raw = Number(p[0]) || 0;
      const code2 = Number(p[1]) || 0;
      const macroIdx = Number.isFinite(Number(p[2]))
        ? Number(p[2]) & 0x0f
        : code1Raw & 0x0f;
      const loopType = (code1Raw >> 4) & 0x0f;
      // 固件只认 code1 低 4 位为槽位，必须写入
      return {
        ...prev,
        type: ZHANLI_T_MACRO,
        code1: (macroIdx & 0x0f) | ((loopType & 0x0f) << 4),
        code2,
        code3: macroIdx,
      };
    }
    const slot = parseUnifiedParam(kf.param) & 0x0f;
    return {
      ...prev,
      type: ZHANLI_T_MACRO,
      code1: slot,
      code2: 1,
      code3: slot,
    };
  }

  /** 火力键：协议 type=0x40；param = (interval<<8)|count 或 [interval, count]
   * 次数上限以实机为准（demo 预设 2/3；过大时固件可能拒写） */
  if (t === MouseKeyFunction.FireKey || t === ZHANLI_T_FIRE) {
    if (Array.isArray(kf.param) && kf.param.length >= 2) {
      return {
        ...prev,
        type: ZHANLI_T_FIRE,
        code1: Number(kf.param[0] ?? 0) & 0xff,
        code2: Number(kf.param[1] ?? 0) & 0xff,
        code3: Number(kf.param[2] ?? 0) & 0xff,
      };
    }
    const p = parseUnifiedParam(kf.param);
    return {
      ...prev,
      type: ZHANLI_T_FIRE,
      code1: (p >> 8) & 0xff,
      code2: p & 0xff,
      code3: 0,
    };
  }

  if (Array.isArray(kf.param)) {
    const code1 = Number(kf.param[0] ?? 0) & 0xff;
    const code2 = Number(kf.param[1] ?? 0) & 0xff;
    const code3 = Number(kf.param[2] ?? 0) & 0xff;
    const protocolType = t & 0xff;
    const dir = parseZhanliScrollDirectionFromProtocol(
      protocolType,
      code1,
      code2
    );
    if (dir) {
      return { ...prev, ...zhanliScrollDirectionToProtocol(dir) };
    }
    return { ...prev, type: protocolType, code1, code2, code3 };
  }

  return {
    ...prev,
    type: t & 0xff,
    code1: parseUnifiedParam(kf.param),
    code2: 0,
    code3: 0,
  };
}

const EMPTY_ZHANLI_MOUSE_KEY: ZhanliMouseKey = {
  index: 0,
  keyValue: 0,
  keyName: "",
  lang: "",
  type: 0,
  code1: 0,
  code2: 0,
  code3: 0,
};

/** 改键中间层写入 store 用：与设备读回 / finalize 后形状一致 */
export function zhanliKeyFunctionToStoreValue(keyFn: {
  type: number;
  param?: number | number[] | string;
}): (string | number)[] {
  return finalizeZhanliKeyStoreValue(
    zhanliMouseKeyToStoreValue(
      unifiedKeyFunctionToZhanliMouseKey(EMPTY_ZHANLI_MOUSE_KEY, keyFn)
    )
  );
}
